// =========================================================================
// Approvals projections. Fork-owned (zone C).
//
// The engine's `approvalQueue()` already makes the selection — which work
// orders are drafts, which orders an agent touched, and the one hard-coded
// restriction. What it does not do, because the demo's markup did it inline,
// is flatten those three differently-shaped collections into one table the
// page can render as a single list.
//
// That flattening is this file's whole job, plus the two small derivations
// that hang off it: the toast a row shows when it is approved, and the rows
// behind the figure in the "A$" column.
//
// Transformation layer (layer 3) — pure, no React, no clock.
// @see docs/logic/approvals/queue.md
// =========================================================================

import {
  approvalQueue,
  fmt$,
  groupNum,
  type OrderCtx,
  type WorkOrderCtx,
} from '../../shared/oem-engine';
import { ALERT_APPROVAL_TOASTS } from '../../shared/seed/alerts';
import { MINES } from '../../data/core';
import type { OemAction } from '../../shared/seed/action';
import type { MineCode } from '../../schema/machine';

/** What kind of thing a queue row asks a person to approve. */
export type ApprovalKind = 'wo' | 'order' | 'restrict';

/** One row of the approval queue, whatever its origin. */
export interface ApprovalRow {
  /** Stable key and the id printed in the first column. */
  readonly id: ApprovalRowId;
  readonly kind: ApprovalKind;
  readonly machine: string;
  readonly mine: MineCode;
  /** Site display name, for the column the demo printed as a bare code. */
  readonly siteName: string;
  /** Which agent drafted it. */
  readonly draftedBy: string;
  /** What approving it will do. */
  readonly what: string;
  /** Direct cost, AUD, or `null` where the row carries no priced amount. */
  readonly amount: number | null;
  /** The toast shown on approval. */
  readonly toast: string;
  /** Where the machine link goes. */
  readonly machineAction: OemAction;
}

/** An approval row's id — the work order, sales order or restriction key. */
export type ApprovalRowId = string;

/**
 * The queue, flattened and ordered: work orders, then parts orders, then the
 * restriction.
 *
 * The order is the demo's and it is not arbitrary — a planner works down the
 * list, and the restriction is last because it is the consequence of the work
 * order above it, not an independent decision.
 *
 * Descriptions go through {@link describe}, which marks the truncation the
 * data pack already applied. The full record is in the source drawer on the
 * row's id.
 */
export function approvalRows(ctx: WorkOrderCtx & OrderCtx): ApprovalRow[] {
  const queue = approvalQueue(ctx);

  const drafts: ApprovalRow[] = queue.drafts.map((w) => ({
    id: w.wo_id,
    kind: 'wo',
    machine: w.machine_id,
    mine: w.mine,
    siteName: MINES[w.mine].name,
    draftedBy: 'Work order agent',
    what: describe(w.description, 180),
    amount: w.total_direct_cost_aud,
    toast: `${w.wo_id} approved — scheduled in SAP PM`,
    machineAction: { view: 'machine', sub: w.machine_id },
  }));

  const orders: ApprovalRow[] = queue.orders.map((o) => ({
    id: o.order_no,
    kind: 'order',
    machine: o.machine_id,
    mine: o.mine,
    siteName: MINES[o.mine].name,
    draftedBy: 'Parts & logistics agent',
    what: `${o.status} · ${o.part_no} × ${o.qty || 1}`,
    amount: null,
    toast: `${o.order_no} sent to the parts portal`,
    machineAction: { view: 'machine', sub: o.machine_id },
  }));

  const restriction: ApprovalRow = {
    id: queue.restriction.id,
    kind: 'restrict',
    machine: queue.restriction.machine,
    mine: queue.restriction.mine,
    siteName: MINES[queue.restriction.mine].name,
    draftedBy: queue.restriction.draftedBy,
    what: queue.restriction.what,
    amount: null,
    toast: ALERT_APPROVAL_TOASTS.restrict,
    machineAction: queue.restriction.action,
  };

  return [...drafts, ...orders, restriction];
}

/**
 * The width of a work-order description in the extracted data pack.
 *
 * Every `Draft` description in `work-orders.json` is exactly 110 characters
 * and most of them stop mid-word — "…three times the limit, clutch sl". The
 * cut is the pack's, made upstream of this port; nothing here can recover the
 * missing tail.
 */
const PACK_DESCRIPTION_CAP = 110;

/** Does this read like a sentence that finished? */
const looksComplete = (text: string): boolean => /[.!?)\]]$/.test(text.trimEnd());

/**
 * A description as the table should print it.
 *
 * Two different truncations meet here and both have to be honest:
 *
 * 1. **The pack's.** A description already cut at
 *    {@link PACK_DESCRIPTION_CAP} gets an ellipsis so the mid-word stop reads
 *    as "there is more", not as a rendering bug. Silently printing
 *    `clutch sl` as though it were the whole sentence is the one thing not to
 *    do — it makes the data look broken and the page look careless.
 * 2. **Ours.** Should a longer description ever arrive, the column still cuts
 *    it, but on a word boundary.
 *
 * The demo's own `esc(w.description).slice(0,120)` did neither, because its
 * data happened never to reach 120.
 */
function describe(text: string, max: number): string {
  const fromPack = text.length >= PACK_DESCRIPTION_CAP && !looksComplete(text);
  if (text.length <= max) return fromPack ? `${text.trimEnd()}…` : text;
  const cut = text.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

/** The three figures over the table. */
export interface ApprovalSummary {
  readonly waiting: number;
  readonly workOrders: number;
  readonly partsOrders: number;
  readonly restrictions: number;
  /** Total priced direct cost across the rows that carry one. */
  readonly pricedTotal: number;
  /** How many rows carry a priced amount. */
  readonly pricedRows: number;
  /** Distinct sites represented. */
  readonly sites: number;
}

/** Counts and totals over a flattened queue. */
export function approvalSummary(rows: readonly ApprovalRow[]): ApprovalSummary {
  const priced = rows.filter((r) => r.amount != null);
  return {
    waiting: rows.length,
    workOrders: rows.filter((r) => r.kind === 'wo').length,
    partsOrders: rows.filter((r) => r.kind === 'order').length,
    restrictions: rows.filter((r) => r.kind === 'restrict').length,
    pricedTotal: priced.reduce((a, r) => a + (r.amount ?? 0), 0),
    pricedRows: priced.length,
    sites: new Set(rows.map((r) => r.mine)).size,
  };
}

/** The A$ cell — an em dash where the row is not priced, never a zero. */
export const amountLabel = (amount: number | null): string =>
  amount == null ? '—' : fmt$(amount);

// ---------------------------------------------------------------------------
// Source
// ---------------------------------------------------------------------------

/**
 * The full work-order record behind one drafted row.
 *
 * The table shows six fields of a twenty-field record; this is the rest,
 * which is the whole point of the "every figure opens its source" promise on
 * an approvals page. A row that is not a work order has no such record, so
 * this returns `null` and the id is printed plain rather than as a control.
 */
export function approvalSource(
  row: ApprovalRow,
  ctx: WorkOrderCtx,
): {
  kind: 'table';
  title: string;
  columns: string[];
  rows: (string | number | null)[][];
  provenance: string;
} | null {
  if (row.kind !== 'wo') return null;
  const wo = ctx.workOrders.find((w) => w.wo_id === row.id);
  if (!wo) return null;

  const entries = Object.entries(wo) as [string, unknown][];
  return {
    kind: 'table',
    title: `${wo.wo_id} · ${wo.machine_id}`,
    columns: ['Field', 'Value'],
    rows: entries.map(([field, value]) => [
      field,
      value === null || value === undefined
        ? null
        : typeof value === 'number' || typeof value === 'string'
          ? value
          : String(value),
    ]),
    provenance: `SAP PM · WORKORDER · 1 of ${groupNum(ctx.workOrders.length)} rows · drafted by the work order agent, not yet released`,
  };
}
